import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Activity, Zap, Moon, Heart, RefreshCw } from 'lucide-react';
import { WhoopData } from '@/hooks/useWhoopAutoFill';
import { format } from 'date-fns';

interface WhoopAutoFillIndicatorProps {
  isConnected: boolean;
  hasData: boolean;
  whoopData: WhoopData | null;
  lastSync?: string | null;
  onRefresh?: () => void;
  className?: string;
}

export default function WhoopAutoFillIndicator({
  isConnected,
  hasData,
  whoopData,
  lastSync,
  onRefresh,
  className = ''
}: WhoopAutoFillIndicatorProps) {
  if (!isConnected) {
    return null;
  }

  if (!hasData) {
    return (
      <div className={`flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border ${className}`}>
        <Activity className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          No WHOOP data for today yet. Check back after your next sync.
        </span>
      </div>
    );
  }

  return (
    <div className={`space-y-3 p-4 rounded-lg bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold">WHOOP Data Auto-Filled</p>
            <p className="text-xs text-muted-foreground">
              {lastSync ? `Last synced ${format(new Date(lastSync), 'h:mm a')}` : 'Connected'}
            </p>
          </div>
        </div>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            title="Refresh WHOOP data"
          >
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {whoopData?.recovery_score !== null && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-background/60">
            <Heart className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Recovery</p>
              <p className="text-sm font-semibold">{whoopData.recovery_score}%</p>
            </div>
          </div>
        )}

        {whoopData?.sleep_duration_minutes !== null && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-background/60">
            <Moon className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Sleep</p>
              <p className="text-sm font-semibold">
                {Math.round((whoopData.sleep_duration_minutes || 0) / 60 * 10) / 10}h
              </p>
            </div>
          </div>
        )}

        {whoopData?.strain_score !== null && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-background/60">
            <Zap className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Strain</p>
              <p className="text-sm font-semibold">{whoopData.strain_score?.toFixed(1)}</p>
            </div>
          </div>
        )}

        {whoopData?.hrv_score !== null && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-background/60">
            <Activity className="w-4 h-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">HRV</p>
              <p className="text-sm font-semibold">{whoopData.hrv_score?.toFixed(0)}ms</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        ✓ Sleep, energy, and exercise fields have been pre-populated from WHOOP
      </p>
    </div>
  );
}
